import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useConvex, useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Doc, Id } from 'convex/_generated/dataModel'

export const Route = createFileRoute('/app/schedule/review')({
  component: ReviewSession,
})

type SuperMemoGrade = 0 | 1 | 2 | 3 | 4 | 5

function ReviewSession() {
  // Get authenticated user from Clerk/Convex
  const currentUser = useCurrentUser()

  // Show loading state while user is being fetched
  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Please sign in to review vocabulary</div>
      </div>
    )
  }

  return <ReviewSessionContent userId={currentUser._id} />
}

function ReviewSessionContent({ userId }: { userId: Id<'users'> }) {
  const convex = useConvex()
  const queryClient = useQueryClient()

  // State
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showAnswer, setShowAnswer] = useState(false)
  const [sessionStartTime] = useState<number>(Date.now())
  const [cardStartTime, setCardStartTime] = useState<number>(Date.now())
  const [reviewedCount, setReviewedCount] = useState(0)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showRetryMode, setShowRetryMode] = useState(false)

  // Query due items (regular reviews)
  const dueItems = useConvexQuery(api.vocabulary.getDueItems, {
    userId,
    limit: 50,
  })

  // Query same-day retry items
  const retryItems = useConvexQuery(api.reviews.getSameDayRetryItems, {
    userId,
    limit: 50,
  })

  // Submit review mutation with error handling
  const submitReviewMutation = useMutation({
    mutationFn: async (params: {
      vocabularyItemId: Id<'vocabularyItems'>
      grade: SuperMemoGrade
      reviewDuration: number
    }) => {
      setErrorMessage(null) // Clear any previous errors
      return await convex.action(api.reviews.submitReview, {
        userId,
        ...params,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dueItems'] })
      setReviewedCount((prev) => prev + 1)
    },
    onError: (error: Error) => {
      console.error('Review submission failed:', error)
      setErrorMessage(
        error.message || 'Failed to save your review. Please try again.'
      )
    },
  })

  const isLoading = showRetryMode ? retryItems === undefined : dueItems === undefined
  const items = showRetryMode ? (retryItems ?? []) : (dueItems ?? [])
  const currentItem = items[currentIndex]
  const hasMore = currentIndex < items.length - 1
  const isSessionComplete = items.length === 0 || currentIndex >= items.length
  const retryCount = retryItems?.length ?? 0

  // Reset card start time when showing new card
  useEffect(() => {
    if (currentItem) {
      setCardStartTime(Date.now())
      setShowAnswer(false)
    }
  }, [currentItem])

  const handleShowAnswer = () => {
    setShowAnswer(true)
  }

  const handleGrade = async (grade: SuperMemoGrade) => {
    if (!currentItem) return

    const reviewDuration = Date.now() - cardStartTime

    try {
      await submitReviewMutation.mutateAsync({
        vocabularyItemId: currentItem._id,
        grade,
        reviewDuration,
      })

      // Only move to next card on success
      if (hasMore) {
        setCurrentIndex((prev) => prev + 1)
      } else {
        setCurrentIndex(items.length) // Mark session as complete
      }
    } catch (error) {
      // Error already handled by mutation's onError, just prevent navigation
      // User can retry by clicking the grade button again
      console.error('Grade submission error:', error)
    }
  }

  const handleSkip = () => {
    if (hasMore) {
      setCurrentIndex((prev) => prev + 1)
    }
  }

  const sessionDuration = Math.floor((Date.now() - sessionStartTime) / 1000)

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Review Session</h1>
            <a
              href="/app/schedule"
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-lg transition-colors"
            >
              Exit
            </a>
          </div>

          {/* Same-Day Retry Mode Toggle */}
          {!isLoading && retryCount > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <svg
                    className="h-5 w-5 text-amber-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-amber-900">
                      {retryCount} item{retryCount !== 1 ? 's' : ''} need{retryCount === 1 ? 's' : ''} same-day retry
                    </h3>
                    <p className="text-xs text-amber-700 mt-1">
                      Per SM-2 algorithm: Items scored below 4 should be retried today
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowRetryMode(!showRetryMode)
                    setCurrentIndex(0) // Reset to first item when switching modes
                    setShowAnswer(false)
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    showRetryMode
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-amber-600 text-white hover:bg-amber-700'
                  }`}
                >
                  {showRetryMode ? 'Back to Regular Reviews' : 'Retry Now'}
                </button>
              </div>
            </div>
          )}

          {/* Mode Indicator */}
          {showRetryMode && (
            <div className="mb-4 text-sm text-amber-700 font-medium">
              📝 Retry Mode: Reviewing items that need improvement
            </div>
          )}

          {/* Progress Bar */}
          {!isLoading && items.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2 text-sm text-gray-700">
                <span>
                  Progress: {Math.min(currentIndex + 1, items.length)} / {items.length}
                </span>
                <span>Reviewed: {reviewedCount}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(((currentIndex + 1) / items.length) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        {isLoading ? (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <p className="text-gray-600">Loading review items...</p>
          </div>
        ) : isSessionComplete ? (
          <SessionComplete
            reviewedCount={reviewedCount}
            totalItems={items.length}
            sessionDuration={sessionDuration}
          />
        ) : (
          <>
            {/* Error Message Banner */}
            {errorMessage && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">Error saving review</h3>
                    <p className="mt-1 text-sm text-red-700">{errorMessage}</p>
                    <p className="mt-1 text-xs text-red-600">
                      Click the grade button again to retry.
                    </p>
                  </div>
                  <button
                    onClick={() => setErrorMessage(null)}
                    className="ml-auto flex-shrink-0 text-red-400 hover:text-red-600"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <FlashCard
              item={currentItem}
              showAnswer={showAnswer}
              onShowAnswer={handleShowAnswer}
              onGrade={handleGrade}
              onSkip={handleSkip}
              isSubmitting={submitReviewMutation.isPending}
            />
          </>
        )}
      </div>
    </div>
  )
}

function FlashCard({
  item,
  showAnswer,
  onShowAnswer,
  onGrade,
  onSkip,
  isSubmitting,
}: {
  item: Doc<'vocabularyItems'>
  showAnswer: boolean
  onShowAnswer: () => void
  onGrade: (grade: SuperMemoGrade) => void
  onSkip: () => void
  isSubmitting: boolean
}) {
  return (
    <div className="bg-white rounded-lg shadow-xl p-8">
      {/* Card Front */}
      <div className="mb-8">
        <div className="text-center mb-6">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">{item.word}</h2>
          {item.partOfSpeech && (
            <p className="text-sm text-gray-500 italic">({item.partOfSpeech})</p>
          )}
        </div>

        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-2">Context:</p>
          <p className="text-gray-900 italic">&ldquo;{item.context}&rdquo;</p>
        </div>
      </div>

      {/* Show Answer Button or Answer Content */}
      {!showAnswer ? (
        <div className="text-center">
          <button
            onClick={onShowAnswer}
            className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white text-lg font-medium rounded-lg transition-colors shadow-md"
          >
            Show Answer
          </button>
          <div className="mt-4">
            <button
              onClick={onSkip}
              className="text-gray-500 hover:text-gray-700 text-sm underline"
            >
              Skip this card
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Answer Content */}
          <div className="mb-8 space-y-4">
            {item.translation && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm font-medium text-blue-900 mb-1">Translation:</p>
                <p className="text-lg text-blue-900 font-semibold">{item.translation}</p>
              </div>
            )}

            {item.definition && (
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <p className="text-sm font-medium text-purple-900 mb-1">Definition:</p>
                <p className="text-purple-900">{item.definition}</p>
              </div>
            )}

            {item.notes && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-1">Notes:</p>
                <p className="text-gray-700">{item.notes}</p>
              </div>
            )}
          </div>

          {/* Grade Buttons */}
          <div className="border-t border-gray-200 pt-6">
            <p className="text-sm font-medium text-gray-700 mb-4 text-center">
              How well did you remember this word?
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <GradeButton
                grade={0}
                label="Total Blackout"
                description="Complete failure"
                color="red"
                onClick={() => onGrade(0)}
                disabled={isSubmitting}
              />
              <GradeButton
                grade={1}
                label="Incorrect"
                description="Wrong answer"
                color="red"
                onClick={() => onGrade(1)}
                disabled={isSubmitting}
              />
              <GradeButton
                grade={2}
                label="Hard"
                description="Barely correct"
                color="orange"
                onClick={() => onGrade(2)}
                disabled={isSubmitting}
              />
              <GradeButton
                grade={3}
                label="Difficult"
                description="Correct with hesitation"
                color="yellow"
                onClick={() => onGrade(3)}
                disabled={isSubmitting}
              />
              <GradeButton
                grade={4}
                label="Good"
                description="Correct after thought"
                color="green"
                onClick={() => onGrade(4)}
                disabled={isSubmitting}
              />
              <GradeButton
                grade={5}
                label="Perfect"
                description="Immediate recall"
                color="green"
                onClick={() => onGrade(5)}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function GradeButton({
  grade,
  label,
  description,
  color,
  onClick,
  disabled,
}: {
  grade: SuperMemoGrade
  label: string
  description: string
  color: 'red' | 'orange' | 'yellow' | 'green'
  onClick: () => void
  disabled: boolean
}) {
  const colorClasses = {
    red: 'bg-red-100 hover:bg-red-200 text-red-900 border-red-300',
    orange: 'bg-orange-100 hover:bg-orange-200 text-orange-900 border-orange-300',
    yellow: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-900 border-yellow-300',
    green: 'bg-green-100 hover:bg-green-200 text-green-900 border-green-300',
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-4 rounded-lg border-2 transition-all ${colorClasses[color]} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="text-lg font-bold mb-1">{label}</div>
      <div className="text-xs">{description}</div>
      <div className="text-xs font-mono mt-1 opacity-60">Grade: {grade}</div>
    </button>
  )
}

function SessionComplete({
  reviewedCount,
  totalItems,
  sessionDuration,
}: {
  reviewedCount: number
  totalItems: number
  sessionDuration: number
}) {
  const minutes = Math.floor(sessionDuration / 60)
  const seconds = sessionDuration % 60

  return (
    <div className="bg-white rounded-lg shadow-xl p-12 text-center">
      <div className="mb-6">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Session Complete!
        </h2>
        <p className="text-gray-600">Great work on your vocabulary review</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="text-3xl font-bold text-blue-900 mb-1">
            {reviewedCount}
          </div>
          <div className="text-sm text-blue-700">Cards Reviewed</div>
        </div>
        <div className="bg-green-50 rounded-lg p-6 border border-green-200">
          <div className="text-3xl font-bold text-green-900 mb-1">
            {totalItems}
          </div>
          <div className="text-sm text-green-700">Total Due Items</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
          <div className="text-3xl font-bold text-purple-900 mb-1">
            {minutes}:{seconds.toString().padStart(2, '0')}
          </div>
          <div className="text-sm text-purple-700">Time Spent</div>
        </div>
      </div>

      <div className="flex gap-4 justify-center">
        <a
          href="/app/schedule"
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
        >
          Back to Dashboard
        </a>
        <a
          href="/app/schedule/analytics"
          className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-900 font-medium rounded-lg transition-colors"
        >
          View Analytics
        </a>
      </div>
    </div>
  )
}
